import { useMemo } from "react";
import { useConversations } from "@/hooks/inbox/useConversations";
import { useInboxUI } from "./InboxProvider";
import { filterConversations, getConversationTimestamp } from "./utils";
import type { InboxConversation } from "@/types/inbox";

/**
 * Conversations sorted newest-first and filtered by the surface's search +
 * channel filter — shared by the list pane and keyboard navigation so they
 * always agree on ordering.
 */
export function useFilteredConversations(enabled: boolean): {
	conversations: InboxConversation[];
	allConversations: InboxConversation[];
	isLoading: boolean;
	isFetching: boolean;
	refetch: () => void;
} {
	const { channelFilter, search } = useInboxUI();
	const { data, isLoading, isFetching, refetch } = useConversations(enabled);

	const allConversations = useMemo(
		() =>
			[...(data || [])].sort(
				(a, b) =>
					new Date(getConversationTimestamp(b) || 0).getTime() -
					new Date(getConversationTimestamp(a) || 0).getTime()
			),
		[data]
	);

	const conversations = useMemo(
		() => filterConversations(allConversations, { channel: channelFilter, search }),
		[allConversations, channelFilter, search]
	);

	return { conversations, allConversations, isLoading, isFetching, refetch };
}
